import {
	Entities,
	EntityStatus,
	FeedApi,
	FeedCorrection,
	FeedCorrectionListInData,
	FeedTank,
	FeedTankGraphItem,
	FeedTankInstallDataEntityTypeEnum,
	FeedTypeLevelMetricEnum,
	Pagination,
	ProductsApi,
	RawData,
	Sensor,
	Transceiver,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import { EntityStatusService } from '@/services/entity-status-service';
import { FeedTankCurrentState } from '../models';
import FeedTankCorrectionsList from '@/pages/account/products/feed/components/tabs/feed-tanks/components/corrections/FeedTankCorrectionsList.vue';

export class FeedTankService extends EntityStatusService<FeedTank> {
	private feedApi: FeedApi = new FeedApi();
	private productApi: ProductsApi = new ProductsApi();

	public entityReadyForSignOff(): boolean {
		if (
			this.entity.status === EntityStatus.Install &&
			this.feedTankIsProvisioned &&
			this.hardwareAllocated
		) {
			return true;
		}
		return false;
	}

	public get feedTankIsProvisioned(): boolean {
		const { nominalVolume, nominalWeight, feedTypeId } = this.entity;
		const provisionAttributes = [nominalVolume ?? null, nominalWeight ?? null, feedTypeId ?? null];
		return !provisionAttributes.includes(null);
	}

	public async getCurrentState(
		feedTank: FeedTank,
	): Promise<{ data?: FeedTankCurrentState; errorMessage?: string }> {
		if (!feedTank.id) {
			return { errorMessage: 'This feed tank has no id' };
		}
		try {
			const currentStateResponse = await this.feedApi.feedTanksIdCurrentStateGet(feedTank.id);
			return { data: currentStateResponse.data.data ?? {} };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to fetch feed tank current state');
		}
	}

	public async getTankRawData(
		feedTank: FeedTank,
		series: string = 'level',
		sortKey?: string,
		prev: boolean = false,
	): Promise<{ data?: { data: Array<RawData>; links?: Pagination }; errorMessage?: string }> {
		if (!feedTank.id) {
			return { errorMessage: 'This feed tank has no id' };
		}
		try {
			const currentStateResponse = await this.feedApi.getFeedTankRawData(feedTank.id, series, {
				params: {
					sortKey: sortKey?.length ? sortKey ?? undefined : undefined,
					prev,
				},
			});
			return {
				data: {
					data: currentStateResponse.data.data ?? [],
					links: <Pagination>currentStateResponse.data?.links,
				},
			};
		} catch (e) {
			return ApiUtils.handleApiError(
				e,
				`Failed to fetch feed tank graph data for series '${series}'`,
			);
		}
	}

	public async cancelFeedTank(
		feedTank: FeedTank,
		actionReason?: string | null,
	): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id || !feedTank.productId) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.productApi.changeProductStatus(
				feedTank.productId,
				Entities.FeedTanks,
				feedTank.id,
				{ newStatus: EntityStatus.Cancelled, actionReason: actionReason ?? undefined },
			);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to cancel feed tank');
		}
	}

	public async removeFeedTank(
		feedTank: FeedTank,
		actionReason?: string | null,
	): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.feedApi.feedTankDeactivate(feedTank.id, {
				actionReason: actionReason ?? undefined,
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to remove feed tank');
		}
	}

	public async moveFeedTankToSignOff(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (!feedTank.productId || !feedTank.id) {
			return { errorMessage: 'This feed tank has no associated product' };
		}
		try {
			await this.productApi.changeProductStatus(
				feedTank.productId,
				Entities.FeedTanks,
				feedTank.id,
				{ newStatus: EntityStatus.SignOff },
			);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to move vat to sign off');
		}
	}

	public async signOffFeedTank(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.feedApi.feedTankSignOff(feedTank.id);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to sign off feed tank');
		}
	}

	public async provision(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (this.missingFeedTankAttributes(feedTank) || !feedTank.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			const data: any = {
				name: feedTank.name,
				numTanks: feedTank.numTanks,
				feedTypeId: feedTank.feedTypeId!,
				type: feedTank.type,
				formulaId: +(feedTank.formulaId ?? 0),
				formulaParameters: feedTank.formulaParameters ?? {},
			};
			if (
				feedTank.nominalVolume?.toString().length &&
				feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Volume
			)
				data.nominalVolume = feedTank.nominalVolume;
			if (
				feedTank.nominalWeight?.toString().length &&
				feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Weight
			)
				data.nominalWeight = feedTank.nominalWeight;
			await this.feedApi.feedTankProvision(feedTank.id, {
				data: data,
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to provision feed tank');
		}
	}

	public async install(feedTank: FeedTank): Promise<{ errorMessage: string } | void> {
		if (!feedTank.id || !feedTank.sensor?.id || !feedTank.transceiver?.id) {
			return { errorMessage: 'Feed tank is missing required attributes' };
		}
		try {
			await this.feedApi.feedTankInstall(feedTank.id, {
				data: {
					entityType: FeedTankInstallDataEntityTypeEnum.FeedTanks,
					sensorId: feedTank.sensor.id,
					transceiverId: feedTank.transceiver.id,
					nodeId: feedTank.sensor.node?.id ?? null,
				},
			});
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to install feed tank');
		}
	}

	public async getCorrections(
		feedTank: FeedTank,
	): Promise<{ errorMessage?: string; data?: FeedCorrection[] }> {
		if (!feedTank?.id) return { errorMessage: 'Feed tank ID is required' };

		try {
			const corrections = (await this.feedApi.getFeedTankCorrections(feedTank.id)).data;
			return {
				data: corrections.data,
			};
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to get feed tank corrections');
		}
	}

	public async updateFeedTankFormulaCorrection(
		feedTank: FeedTank,
		correction: FeedCorrection,
	): Promise<{ errorMessage?: string; data?: FeedTank }> {
		if (!feedTank?.id) return { errorMessage: 'Feed tank ID is required' };

		try {
			const updatedFeedTank = (
				await this.feedApi.updateFeedTankFormulaCorrection(feedTank.id, {
					formulaCorrection: correction,
				})
			).data?.data;
			return {
				data: updatedFeedTank,
			};
		} catch (e) {
			return ApiUtils.handleApiError(e, 'Failed to update feed tank formula correction');
		}
	}

	private get hardwareAllocated(): boolean {
		return !!this.entity.transceiver?.id && !!this.entity.sensor;
	}

	private missingFeedTankAttributes(feedTank: FeedTank): boolean {
		return (
			(feedTank.nominalVolume == null &&
				feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Volume) ||
			(feedTank.nominalWeight == null &&
				feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Weight) ||
			feedTank.feedTypeId == null ||
			feedTank.formulaId == null
		);
	}
}
